import {create} from 'mutative'
import type {Dispatch} from 'react'
import {
  Button,
  Dialog,
  DialogTrigger,
  Popover,
  Tab,
  TabList,
  TabPanel,
  Tabs,
} from 'react-aria-components'
import {Range} from 'react-range'

import ChevronDownSvg from '@/assets/icons/chevron-down.svg?react'
import PrimaryButton from '@/components/PrimaryButton/PrimaryButton'
import type {TimesFilter} from '@/lib/flight/types'
import {BookingType} from '@/lib/flight/types'
import VisuallyHidden from '@/utils/a11y/VisuallyHidden'

import style from './FilterTimes.module.scss'

interface TimeFilterProps {
  from: number
  to: number
  setFrom: Dispatch<number>
  setTo: Dispatch<number>
  label: string
}

function convertNumberHourToTextHour(hour: number) {
  if (hour === 0) return '12 AM'
  if (hour === 12) return '12 PM'
  if (hour === 24) return '12 AM'
  if (hour > 12) return `${hour - 12} PM`
  return `${hour} AM`
}

function TimeFilter({from, to, setFrom, setTo, label}: TimeFilterProps) {
  const isAnytime = from === 0 && to === 24

  // const clear = useCallback(() => {
  //   setFrom(0)
  //   setTo(24)
  // }, [setFrom, setTo])

  const valueText = [convertNumberHourToTextHour(from), convertNumberHourToTextHour(to)]

  return (
    <div className={style.TimeFilter}>
      <div className={style.TimeFilterLabel}>
        <span aria-hidden>{label}: </span>
        <VisuallyHidden>{`${label} time:`}</VisuallyHidden>
        <span className={style.Highlight}>
          {isAnytime ? (
            'Anytime'
          ) : (
            <>
              <span aria-hidden>
                {convertNumberHourToTextHour(from)} - {convertNumberHourToTextHour(to)}
              </span>
              <VisuallyHidden>{`From ${convertNumberHourToTextHour(from)} to ${convertNumberHourToTextHour(to)}`}</VisuallyHidden>
            </>
          )}
        </span>
      </div>
      <Range
        label={label}
        step={1}
        min={0}
        max={24}
        values={[from, to]}
        onChange={values => {
          if (values[0] === undefined || values[1] === undefined) return
          if (values[0] >= values[1]) return
          setFrom(values[0])
          setTo(values[1])
        }}
        renderTrack={({props, children}) => (
          <div {...props} style={props.style} className={style.Track}>
            {children}
          </div>
        )}
        renderThumb={({props, index}) => (
          <div
            {...props}
            key={props.key}
            style={props.style}
            aria-label={index === 0 ? `Earliest ${label} time` : `Latest ${label} time`}
            className={style.Thumb}
            aria-valuetext={valueText[index]}
          />
        )}
      />
    </div>
  )
}

interface FilterTimePanelProps {
  times: TimesFilter[] | null
  setTimes: Dispatch<SetStateAction<TimesFilter[] | null>>
  index: number
}

function FilterTimePanel({times, setTimes, index}: FilterTimePanelProps) {
  const setDepartureFrom = useCallback(
    (value: number) => {
      setTimes(times => {
        if (!times) {
          const newTimes = []
          newTimes[index] = {
            departure: {from: value, to: 24},
            arrival: {from: 0, to: 24},
          }
          return newTimes
        }
        return create(times, draft => {
          if (!draft[index]) {
            draft[index] = {
              departure: {from: value, to: 24},
              arrival: {from: 0, to: 24},
            }
          } else {
            draft[index].departure.from = value
          }
        })
      })
    },
    [setTimes, index],
  )

  const setDepartureTo = useCallback(
    (value: number) => {
      setTimes(times => {
        if (!times) {
          const newTimes = []
          newTimes[index] = {
            departure: {from: 0, to: value},
            arrival: {from: 0, to: 24},
          }
          return newTimes
        }
        return create(times, draft => {
          if (!draft[index]) {
            draft[index] = {
              departure: {from: 0, to: value},
              arrival: {from: 0, to: 24},
            }
          } else {
            draft[index].departure.to = value
          }
        })
      })
    },
    [setTimes, index],
  )

  const setArrivalFrom = useCallback(
    (value: number) => {
      setTimes(times => {
        if (!times) {
          const newTimes = []
          newTimes[index] = {
            departure: {from: 0, to: 24},
            arrival: {from: value, to: 24},
          }
          return newTimes
        }

        return create(times, draft => {
          if (!draft[index]) {
            draft[index] = {
              departure: {from: 0, to: 24},
              arrival: {from: value, to: 24},
            }
          } else {
            draft[index].arrival.from = value
          }
        })
      })
    },
    [setTimes, index],
  )

  const setArrivalTo = useCallback(
    (value: number) => {
      setTimes(times => {
        if (!times) {
          const newTimes = []
          newTimes[index] = {
            departure: {from: 0, to: 24},
            arrival: {from: 0, to: value},
          }
          return newTimes
        }
        return create(times, draft => {
          if (!draft[index]) {
            draft[index] = {
              departure: {from: 0, to: 24},
              arrival: {from: 0, to: value},
            }
          } else {
            draft[index].arrival.to = value
          }
        })
      })
    },
    [setTimes, index],
  )

  return (
    <>
      <TimeFilter
        from={times?.[index]?.departure.from ?? 0}
        to={times?.[index]?.departure.to ?? 24}
        setFrom={setDepartureFrom}
        setTo={setDepartureTo}
        label='Departure'
      />
      <TimeFilter
        from={times?.[index]?.arrival.from ?? 0}
        to={times?.[index]?.arrival.to ?? 24}
        setFrom={setArrivalFrom}
        setTo={setArrivalTo}
        label='Arrival'
      />
    </>
  )
}

interface Props {
  bookingType: BookingType
  times: TimesFilter[] | null
  setTimes: Dispatch<SetStateAction<TimesFilter[] | null>>
}

export default memo<Props>(function FilterTimes({times, bookingType, setTimes}) {
  return (
    <DialogTrigger>
      <Button className={style.Button}>
        <div className={style.Value}>
          <span>
            Times<VisuallyHidden>&nbsp;filter</VisuallyHidden>
          </span>
        </div>
        <span aria-hidden className={style.Chevron}>
          <ChevronDownSvg
            className={style.ChevronIcon}
            width={12}
            height={16}
            viewBox='0 0 24 24'
            aria-hidden
          />
        </span>
      </Button>
      <Popover className={style.Popover}>
        <Dialog className={style.Dialog}>
          <div className={style.Heading} aria-hidden>
            Times
          </div>
          {bookingType === BookingType.RoundTrip && (
            <Tabs className={style.Tabs}>
              <TabList aria-label='Flight segment' className={style.TabList}>
                <Tab id='Outbound' className={style.Tab}>
                  Outbound <VisuallyHidden>flight</VisuallyHidden>
                </Tab>
                <Tab id='Return' className={style.Tab}>
                  Return <VisuallyHidden>flight</VisuallyHidden>
                </Tab>
              </TabList>
              <TabPanel id='Outbound' className={style.TabPanel}>
                <FilterTimePanel times={times} setTimes={setTimes} index={0} />
              </TabPanel>
              <TabPanel id='Return' className={style.TabPanel}>
                <FilterTimePanel times={times} setTimes={setTimes} index={1} />
              </TabPanel>
            </Tabs>
          )}
          {bookingType === BookingType.OneWay && (
            <div className={style.TabPanel}>
              <FilterTimePanel times={times} setTimes={setTimes} index={0} />
            </div>
          )}
          {/* TODO: support multi-city */}
          {/* {bookingType === BookingType.MultiCity && (
            <></>
          )} */}
          <div className={style.ClearButtonContainer}>
            <PrimaryButton
              size='sm'
              className={style.ClearButton}
              onPress={() => {
                setTimes(null)
              }}
            >
              Clear
            </PrimaryButton>
          </div>
        </Dialog>
      </Popover>
    </DialogTrigger>
  )
})
